/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs, { Dayjs } from "dayjs";
import dayjsBusinessTime from "dayjs-business-time-advanced";
import { z } from "zod";

import { TDictionary } from "../../../util/types";
import { DeliveryAddress } from "../../cart/storage/cart.types";
import { TypesenseProduct } from "../../typesense/types";
import { allSeedsCount } from "../utils/calculateTotal";

dayjs.extend(dayjsBusinessTime);

// Paddock
export const PaddockSchema = z.object({
  hectares: z.coerce.number({ required_error: "Hectares is required." }).min(1).max(1e4),
  name: z.string({ required_error: "Paddock name is required." }).min(1),
  deliveryDate: z.coerce
    .date()
    .transform((v) => dayjs(v))
    .refine((val: Dayjs) => {
      return val.day() <= 5 && val.day() >= 1;
    }, "Delivery date must be a weekday.")
    .refine((val: Dayjs) => {
      return val.isAfter(dayjs().startOf("day").addBusinessDays(2));
    }, "Delivery date needs to be at least two business days after today.")
    .refine((val: Dayjs) => {
      return val.isBefore(dayjs().add(1, "year").add(-1, "day").toDate());
    }, "Delivery date must be within one year."),
});

export type Paddock = z.infer<typeof PaddockSchema>;

export const PaddockDefaultValues: Paddock = {
  hectares: 1,
  name: "Paddock 1",
  deliveryDate: dayjs().startOf("day").addBusinessDays(3),
};

// Product Quantity
export const SeedProductLineSchema = z.object({
  lineId: z.string().nullish(),
  productId: z.coerce.number(),
  quantity: z.coerce.number({ required_error: "Quantity is required." }).min(0),
  quantityRoundedUp: z.boolean().default(false).optional(),
  sowingRate: z.coerce.number({ required_error: "Sowing rate is required." }).min(0).max(10000),
  treatment: z.boolean(),
  isMixingFee: z.boolean().nullish(),
  discountedPriceExcGst: z.coerce.number().min(0).nullish(),
  editedPriceExcGst: z.coerce.number().min(0).nullish(),
  lastEditedReasonCode: z.string().nullish(),
  lastEditedReasonNote: z.string().nullish(),
});

export type SeedProductLine = z.infer<typeof SeedProductLineSchema>;

export const ProductQuantityDefaultValue: SeedProductLine = {
  productId: 0,
  quantity: 1,
  sowingRate: 1,
  treatment: false,
};

export enum SEED_MIX_TYPE {
  PREMIX = "premix",
  CUSTOM = "custom",
}

// Seed Mix
export const SeedMixSchema = z
  .object({
    id: z.string().nullish(),
    deliveryAddress: z.custom<DeliveryAddress>().nullish(),
    storeId: z.string().nullish(),
    name: z.string(),
    paddocksExpanded: z.boolean().optional(),
    productsExpanded: z.boolean().optional(),
    notesExpanded: z.boolean().optional(),
    paddocks: z.array(PaddockSchema).min(1, "There must be at least 1 paddock."),
    products: z.array(SeedProductLineSchema),
    mixType: z.nativeEnum(SEED_MIX_TYPE).optional(),
    deliveryInstructions: z.string().nullish(),
    internalNotes: z
      .string()
      .max(200)
      .nullish()
      .transform((v) => {
        if (v) {
          return v;
        }
        return "";
      })
      .optional(),
    externalNotes: z
      .string()
      .max(200)
      .nullish()
      .transform((v) => {
        if (v) {
          return v;
        }
        return "";
      })
      .optional(),
    subtotalAmountExcGst: z.number().nullish(),
    gstAmount: z.number().nullish(),
  })
  // .omit({ paddocksExpanded: true, productsExpanded: true, notesExpanded: true })
  .refine(
    ({ deliveryAddress, storeId }) => deliveryAddress !== undefined || storeId !== undefined,
    { message: "One of pick up from branch, store or delivery method must be specified" }
  )
  .refine(
    ({ products, mixType }) => {
      if (mixType === SEED_MIX_TYPE.CUSTOM) {
        const productsWithLineIds = products.map((e) => e.lineId).length;
        const seedCount = allSeedsCount(products);
        return seedCount > 1 && productsWithLineIds > 0;
      }

      return true;
    },
    {
      path: ["products"],
      message: "This custom seed mix needs at least 2 products.",
    }
  )
  .refine(
    ({ products, mixType }) => {
      if (mixType === SEED_MIX_TYPE.PREMIX) {
        return allSeedsCount(products) > 0;
      }
      return true;
    },
    {
      path: ["products"],
      message: "This seed mix needs at least 1 product.",
    }
  );
// .superRefine(({ products, mixType }, ctx) => {
//   if (mixType === SEED_MIX_TYPE.CUSTOM && products.length <= 1) {
//     ctx.addIssue({
//       code: z.ZodIssueCode.custom,
//       path: ['products'],
//       message: 'This custom seed mix has only 1 product.',
//     })
//   }
// })

export type SeedMix = z.infer<typeof SeedMixSchema>;

export type TSeedMixProduct = z.infer<
  typeof SeedMixSchema._def.schema._def.schema._def.schema.shape.products._def.type
>;

export const SeedMixDefaultValue: SeedMix = {
  name: "",
  paddocks: [PaddockDefaultValues],
  products: [],
  mixType: SEED_MIX_TYPE.PREMIX,
  paddocksExpanded: true,
  productsExpanded: true,
  notesExpanded: false,
};

// Seed Order
export const SeedOrderSchema = z.object({
  id: z.string().nullish(),
  accountNumber: z.string().nullish(),
  version: z.string().nullish(),
  mixes: z.array(SeedMixSchema).min(1, "There must be at least 1 seed."),
});

export type SeedOrdersInfo = z.infer<typeof SeedOrderSchema>;

export const SeedOrderDefaultValue: SeedOrdersInfo = {
  mixes: [],
};

export interface SeedOrderCheckout {
  accountNumber: string;
  orderVersion: string;
  customerName: string;
  email: string;
  phone: string;
  customerReference: string;
}

export interface SeedStoreInfo {
  id: string;
  name: string;
  address: string;
}

export interface SeedStoresState {
  stores: SeedStoreInfo[];
}

export interface SeedCartState {
  loading: boolean;
  seedOrder: TDictionary<SeedOrdersInfo>;
  syncing: boolean;
  syncedAt: TDictionary<Date>;
  updatedAt: TDictionary<Date>;
  errors: TDictionary<string | undefined>;
}

export interface SeedTypesenseProduct extends TypesenseProduct {
  is_loose_product?: boolean;
  sowing_rate?: number;
  unit?: string;
}
